"use client";
import React from 'react';
import { motion } from 'framer-motion';

const marqueeItems = [
    'Brand Trust', 'Organic Reach', 'Content Strategy', 'Community Building',
    'Paid Social', 'Brand Authority', 'Lead Generation', 'Audience Growth',
    'Meta Ads', 'Engagement', 'Social Proof', 'Platform Strategy',
];

const industries = [
    { label: 'Startups & Growing Brands' },
    { label: 'Healthcare & Wellness Businesses' },
    { label: 'Real Estate Developers' },
    { label: 'SaaS & Technology Companies' },
    { label: 'Ecommerce Brands' },
    { label: 'Hospitality & Lifestyle Businesses' },
    { label: 'Professional Service Firms' },
];

export default function SocialGoals() {
    return (
        <>
            {/* ── Marquee ticker strip ── */}
            <div
                className="relative overflow-hidden py-4"
                style={{ background: '#080400', borderBottom: '1px solid rgba(206,255,26,0.10)' }}
            >
                <style>{`
                    @keyframes marquee-scroll {
                        0% { transform: translateX(0); }
                        100% { transform: translateX(-50%); }
                    }
                    .marquee-track { animation: marquee-scroll 28s linear infinite; }
                    .marquee-track:hover { animation-play-state: paused; }
                `}</style>
                <div className="flex items-center gap-0 marquee-track whitespace-nowrap" style={{ width: 'max-content' }}>
                    {[...marqueeItems, ...marqueeItems].map((item, i) => (
                        <React.Fragment key={i}>
                            <span
                                className="text-xs font-bold tracking-[0.18em] uppercase px-6"
                                style={{ color: i % 2 === 0 ? '#ceff1a' : 'rgba(251,253,253,0.22)', fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}
                            >
                                {item}
                            </span>
                            <span style={{ color: 'rgba(206,255,26,0.25)', fontSize: 6 }}>◆</span>
                        </React.Fragment>
                    ))}
                </div>
            </div>

            {/* ── Who We Work With ── */}
            <section
                className="relative overflow-hidden py-24 px-6 md:px-16 lg:px-24"
                style={{ background: '#ffffff', borderBottomLeftRadius: '72px', borderBottomRightRadius: '72px' }}
            >
                {/* Dot grid */}
                <div className="absolute inset-0 pointer-events-none" style={{
                    backgroundImage: 'radial-gradient(circle, rgba(55,48,163,0.06) 1.5px, transparent 1.5px)',
                    backgroundSize: '30px 30px',
                    maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)',
                    WebkitMaskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)',
                }} />

                <div className="max-w-[1400px] mx-auto flex flex-col gap-16 relative z-10">

                    {/* Header row */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-end">
                        <div className="flex flex-col gap-5">
                            <motion.span
                                initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                                className="text-xs font-bold tracking-[0.22em] uppercase"
                                style={{ color: '#3730a3', fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}
                            >
                                Who We Work With
                            </motion.span>
                            <motion.h2
                                initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.08 }}
                                style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontWeight: 800, fontSize: 'clamp(38px, 4.5vw, 68px)', lineHeight: 1.0, letterSpacing: '-0.02em', color: '#080400' }}
                            >
                                Who We<br />Work With
                            </motion.h2>
                        </div>
                        <motion.p
                            initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }}
                            className="text-base md:text-lg leading-relaxed lg:max-w-md lg:self-end"
                            style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', color: 'rgba(8,4,0,0.50)', letterSpacing: '0.02em' }}
                        >
                            Every audience behaves differently. Our job is to understand what captures attention, builds trust, and keeps your brand relevant in their world.
                        </motion.p>
                    </div>

                    {/* Industry list — two-column staggered */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                        {industries.map((item, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, x: -16 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.45, delay: i * 0.07 }}
                                className={`flex items-center gap-5 py-5 group ${i % 2 !== 0 ? 'md:pl-10' : ''}`}
                                style={{ borderBottom: '1px solid rgba(55,48,163,0.10)' }}
                            >
                                {/* Pill number */}
                                <div
                                    className="shrink-0 flex items-center justify-center rounded-full font-bold text-xs text-[#3730a3] transition-all duration-300 group-hover:bg-[#3730a3] group-hover:text-white group-hover:border-[#3730a3]"
                                    style={{ width: 36, height: 36, border: '1.5px solid rgba(55,48,163,0.20)', fontFamily: 'var(--font-clash), "Clash Display", sans-serif', letterSpacing: '0.05em' }}
                                >
                                    {String(i + 1).padStart(2, '0')}
                                </div>
                                <span
                                    className="text-sm md:text-base font-semibold transition-colors duration-200 group-hover:text-[#3730a3]"
                                    style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', color: 'rgba(8,4,0,0.75)', letterSpacing: '0.01em' }}
                                >
                                    {item.label}
                                </span>
                                <svg className="ml-auto shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                    <path d="M2 7h10M7 2l5 5-5 5" stroke="#3730a3" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </motion.div>
                        ))}
                    </div>

                    {/* Bottom note */}
                    <motion.p
                        initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
                        className="text-base"
                        style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', color: 'rgba(8,4,0,0.40)', letterSpacing: '0.03em', borderTop: '1px solid rgba(55,48,163,0.08)', paddingTop: 24 }}
                    >
                        We provide social media marketing services in Bangalore for businesses looking to build authority, engagement, and long-term brand value.
                    </motion.p>

                </div>
            </section>
        </>
    );
}
